import { Instance, cast, types } from "mobx-state-tree";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { ColumnType } from "../../components/datatable/interfaces";
import { BatchAnalysisStatus } from "../../generated/VideoAnalysis";

export const BatchAnalysisStatusEnumModel = types.model({
  status: types.enumeration(
    Object.keys(BatchAnalysisStatus)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => String(key))
  ),
});

export const BatchProcessingInstanceModel = types.model({
  startedAt: new Date(),
  projectName: types.string,
  videoIds: types.array(types.number),
});
export type BatchProcessingInstance = Instance<typeof BatchProcessingInstanceModel>;

export const BatchProcessingStatusModel = types
  .model({
    displaySnackbar: false,
    displayStatusModal: false,
    displayFinalizedModal: false,
    batchResultsStorage: DataGridStorageModel,
    instancesResultsStorages: types.array(DataGridStorageModel),
    batchProcessingInstances: types.optional(types.array(BatchProcessingInstanceModel), []),
    failedStatuses: types.array(BatchAnalysisStatusEnumModel),
  })
  .views((self) => ({
    get videoCount() {
      return self.batchResultsStorage.data?.items?.length ?? 0;
    },
    get objectCount() {
      return (
        self.batchResultsStorage.data?.items
          ?.map((i: any) => i.objectId)
          .filter((value: any, index: any, array: any) => array.indexOf(value) === index).length ??
        0
      );
    },
    get successfullyCount() {
      return (
        self.batchResultsStorage.data?.items?.filter(
          (value: any) => value.status === BatchAnalysisStatus.Successfully
        ).length ?? 0
      );
    },
    get failedCount() {
      return (
        self.batchResultsStorage.data?.items?.filter((value: any) =>
          self.failedStatuses.map((object) => object.status).includes(value.status.toString())
        ).length ?? 0
      );
    },
    get tableColumns() {
      return [
        {
          key: "object",
          name: "damages.BatchProcessingStatus.batchResults.object",
          type: ColumnType.String,
        },
        {
          key: "inspection",
          name: "damages.BatchProcessingStatus.batchResults.inspection",
          type: ColumnType.String,
        },
        {
          key: "status",
          name: "general.status",
          type: ColumnType.String,
        },
      ];
    },
  }))
  .views((self) => ({
    get percentComplete() {
      return ((self.successfullyCount + self.failedCount) * 100) / self.videoCount;
    },
    get isProcessing() {
      return self.successfullyCount + self.failedCount !== self.videoCount;
    },
  }))
  .actions((self) => ({
    showStatusBatchModal() {
      self.displayStatusModal = true;
      self.displaySnackbar = false;
      self.displayFinalizedModal = false;
    },
    hideStatusBatchModal() {
      self.displayStatusModal = false;
      if (self.isProcessing) self.displaySnackbar = true;
      else {
        self.displayFinalizedModal = false;
        self.batchResultsStorage.setData(null);
        self.instancesResultsStorages.forEach((instance) => instance.setData(null));
        self.batchProcessingInstances = cast([]);
      }
    },
  }))
  .actions((self) => {
    return {
      addBatchVideoAnalysisInstance(batchProcessingInstance: BatchProcessingInstance) {
        self.batchProcessingInstances.push(batchProcessingInstance);
      },

      getSuccessfullyCountForVideoIds(videoIds: number[]) {
        return (
          self.batchResultsStorage.data?.items?.filter(
            (value: any) =>
              value.status === BatchAnalysisStatus.Successfully && videoIds.includes(value.videoId)
          ).length ?? 0
        );
      },

      getFailedCountForVideoIds(videoIds: number[]) {
        return (
          self.batchResultsStorage.data?.items?.filter(
            (value: any) =>
              self.failedStatuses
                .map((object) => object.status)
                .includes(value.status.toString()) && videoIds.includes(value.videoId)
          ).length ?? 0
        );
      },
    };
  });
